import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../store'
import { loginThunk, clearError } from '../../store/slices/authSlice'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Eye, EyeOff, Lock, Mail, Zap, ShieldCheck, IndianRupee } from 'lucide-react'

const schema = z.object({
  email:    z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
})
type FormData = z.infer<typeof schema>

const FEATURES = [
  { icon: Zap,          text: 'Get referred by insiders at top companies' },
  { icon: IndianRupee,  text: 'Earn rewards for every successful referral' },
  { icon: ShieldCheck,  text: 'Secure escrow — payments released only on hire' },
]

export default function LoginPage() {
  const dispatch  = useAppDispatch()
  const navigate  = useNavigate()
  const { loading, error } = useAppSelector((s) => s.auth)
  const [showPwd, setShowPwd] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  useEffect(() => { dispatch(clearError()) }, [dispatch])

  const onSubmit = async (data: FormData) => {
    const result = await dispatch(loginThunk(data))
    if (loginThunk.fulfilled.match(result)) {
      toast.success('Welcome back!')
      navigate('/')
    } else {
      toast.error((result.payload as string) || 'Login failed. Please try again.')
    }
  }

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#FDFAFF' }}>

      {/* ── Left: Branding ── */}
      <div
        className="hidden lg:flex flex-col justify-center px-16 w-[52%] relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #6D28D9 0%, #8B5CF6 50%, #A78BFA 100%)' }}
      >
        {/* Decorative circles */}
        <div className="absolute top-[-80px] right-[-60px] w-72 h-72 rounded-full opacity-20" style={{ background: 'white' }} />
        <div className="absolute bottom-[-60px] left-[-40px] w-56 h-56 rounded-full opacity-10" style={{ background: 'white' }} />

        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)' }}>
              <span className="text-white font-bold text-2xl leading-none select-none">R</span>
            </div>
            <span className="text-3xl font-bold text-white tracking-tight">Referrals</span>
          </div>

          <h1 className="text-4xl font-light text-white leading-snug max-w-sm mb-2">
            Welcome back to your
          </h1>
          <h2 className="text-4xl font-bold text-white leading-snug max-w-sm mb-8">
            professional network.
          </h2>

          <div className="space-y-4">
            {FEATURES.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.2)' }}>
                  <Icon size={15} className="text-white" />
                </div>
                <p className="text-sm text-white/85">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Right: Form ── */}
      <div className="flex-1 flex items-center justify-center py-12 px-6 lg:px-16">
        <div className="w-full max-w-sm">

          {/* Mobile logo */}
          <div className="flex items-center gap-2.5 mb-8 lg:hidden">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #8B5CF6, #6D28D9)' }}>
              <span className="text-white font-bold text-lg leading-none">R</span>
            </div>
            <span className="text-2xl font-bold" style={{ color: '#7C3AED' }}>Referrals</span>
          </div>

          {/* Card */}
          <div className="bg-white rounded-2xl p-8" style={{ border: '1px solid #EDE9FE', boxShadow: '0 8px 40px rgba(139,92,246,0.16)' }}>
            <h2 className="text-2xl font-bold mb-1" style={{ color: '#13082A' }}>Sign in</h2>
            <p className="text-sm mb-6" style={{ color: '#6B6080' }}>Welcome back! Please enter your details.</p>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-1.5" style={{ color: '#13082A' }}>Email</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#A89EC0' }} />
                  <input {...register('email')} type="email" autoComplete="email" className="input pl-10" placeholder="you@example.com" />
                </div>
                {errors.email && <p className="text-xs mt-1" style={{ color: '#DC2626' }}>{errors.email.message}</p>}
              </div>

              <div>
                <div className="flex justify-between items-center mb-1.5">
                  <label className="block text-sm font-semibold" style={{ color: '#13082A' }}>Password</label>
                  <Link to="/forgot-password" className="text-xs font-semibold no-underline" style={{ color: '#7C3AED' }}>
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: '#A89EC0' }} />
                  <input
                    {...register('password')}
                    type={showPwd ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="input pl-10 pr-10"
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3.5 top-1/2 -translate-y-1/2 transition-colors" style={{ color: '#A89EC0' }}>
                    {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {errors.password && <p className="text-xs mt-1" style={{ color: '#DC2626' }}>{errors.password.message}</p>}
              </div>

              {error && (
                <div className="text-sm px-4 py-3 rounded-xl" style={{ background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA' }}>
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full py-3 text-base mt-2"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px" style={{ background: '#EDE9FE' }} />
              <span className="text-xs font-medium" style={{ color: '#A89EC0' }}>or</span>
              <div className="flex-1 h-px" style={{ background: '#EDE9FE' }} />
            </div>

            <p className="text-center text-sm" style={{ color: '#6B6080' }}>
              New to Referrals?{' '}
              <Link to="/register" className="font-bold no-underline" style={{ color: '#7C3AED' }}>
                Join now
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
